import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Typography } from '@mui/material';

import CodeLayout from '../CodeLayout/CodeLayout';
import RecipientCodeMessage from '../RecipientCodeMessage/RecipientCodeMessage';
import RecipientCodeForm from '../RecipientCodeForm/RecipientCodeForm';
import { resetCodeError, checkCodeRequest } from '../../store/code.actions';
import { getIsLoading, getErrorCode, getRedirectUrl, getParams } from '../../store/code.selectors';
import WarningModal from '../../../../components/WarningModal/WarningModal';
import useGlobalSettings from '../../../../hooks/useGlobalSettings';
import useTrackCode from '../../hooks/useTrackCode';
import useAcceptPolicy from '../../../../hooks/useAcceptPolicy';

const CodePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const trackEvent = useTrackCode();
  const { isSegmentLoaded, isSegmentInit, handleSegmentInit } = useGlobalSettings();
  const handleAcceptPolicy = useAcceptPolicy();

  const isLoading = useSelector(getIsLoading);
  const redirectUrl = useSelector(getRedirectUrl);
  const errorCode = useSelector(getErrorCode);
  const params = useSelector(getParams);

  const isWarningOpen = !!errorCode && !isLoading;

  const closeModalHandler = useCallback(() => {
    dispatch(resetCodeError());
    trackEvent('Warning popup - Clicked try again button');
  }, [dispatch, trackEvent]);

  const submitCodeHandler = useCallback(
    code => {
      dispatch(checkCodeRequest(code));
      handleAcceptPolicy('Code - Clicked submit code button', { code });
    },
    [dispatch, handleAcceptPolicy],
  );

  useEffect(() => {
    if (isSegmentLoaded) {
      handleSegmentInit();
    }
  }, [isSegmentLoaded, handleSegmentInit]);

  useEffect(() => {
    if (isSegmentInit) {
      trackEvent('Code Page - Opened');
    }
  }, [isSegmentInit, trackEvent]);

  return (
    <>
      <CodeLayout message={<RecipientCodeMessage />} actions={<RecipientCodeForm onSubmit={submitCodeHandler} />} />
      <WarningModal
        title={params?.header ?? ''}
        isOpen={isWarningOpen}
        submitButtonText="Ok"
        cancelButtonText={undefined}
        onSubmit={closeModalHandler}
      >
        <Typography data-testid="giftCodeWarningModalMessage" className="Body-Regular-Left-Static">
          {params?.message ?? ''}
        </Typography>
      </WarningModal>
      {redirectUrl && <Redirect to={redirectUrl} />}
    </>
  );
};

export default CodePage;
